import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useContent } from '../hooks/useContent';

const sections = [
  { id: 'hero', label: 'Home' },
  { id: 'recognition', label: 'Recognition' },
  { id: 'experience', label: 'Experience' },
  { id: 'difference', label: 'Why Different' },
  { id: 'beyond', label: 'Beyond Technique' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'amatrius', label: 'Amatrius' },
  { id: 'extended', label: 'Extended Work' },
  { id: 'about', label: 'About' },
  { id: 'contact', label: 'Contact' },
];

export const HolisMassage: React.FC = () => {
  const { content, isLoading } = useContent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [privateForm, setPrivateForm] = useState({
    name: '',
    email: '',
    location: '',
    message: '',
  });
  const [complimentaryForm, setComplimentaryForm] = useState({
    question1: '',
    question2: '',
    question3: '',
    question4: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [processStep, setProcessStep] = useState(0);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handlePrivateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleComplimentarySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  // Show loading state while content is being fetched
  if (isLoading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-bronze"></div>
          <p className="mt-4 text-taupe">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ivory">

      {/* Slide-out Menu */}
      <motion.div
        initial={false}
        animate={{
          x: isMenuOpen ? 0 : '100%',
        }}
        transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
        className="fixed top-0 right-0 h-full w-full sm:w-80 bg-ivory z-40 shadow-2xl"
      >
        <div className="flex flex-col h-full pt-20 px-6 sm:px-8 pb-8">
          <nav className="flex flex-col space-y-5 sm:space-y-6 flex-grow overflow-y-auto">
            {sections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                onClick={closeMenu}
                className="text-xl sm:text-2xl font-cormorant text-charcoal hover:text-bronze transition-colors"
              >
                {section.label}
              </a>
            ))}
          </nav>

          {/* Language Switcher */}
          <div className="mt-auto pt-8 border-t border-taupe/20">
            <div className="flex items-center justify-center gap-3 font-cormorant text-lg mb-6">
              <button className="text-charcoal hover:text-bronze transition-colors font-medium">
                EN
              </button>
              <span className="text-taupe">|</span>
              <button className="text-taupe hover:text-bronze transition-colors">
                DE
              </button>
            </div>
            
            {/* Created by Credit */}
            <p className="text-xs text-taupe text-center">
              Created by <a href="https://wavelynecode.com" target="_blank" rel="noopener noreferrer" className="hover:text-bronze transition-colors">Wavelyne</a>
            </p>
          </div>
        </div>
      </motion.div>

      {/* Overlay */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeMenu}
          className="fixed inset-0 bg-charcoal/20 backdrop-blur-sm z-30"
        />
      )}

      {/* Dots Navigation */}
      <div className="fixed right-8 top-1/2 -translate-y-1/2 z-30 hidden md:flex flex-col gap-4">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => {
              const element = document.getElementById(section.id);
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="group relative"
            aria-label={`Navigate to ${section.label}`}
          >
            <div
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeSection === section.id
                  ? 'bg-bronze w-8'
                  : 'bg-taupe/40 hover:bg-taupe/70'
              }`}
            />
            <span className="absolute right-6 top-1/2 -translate-y-1/2 bg-charcoal text-ivory text-xs px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
              {section.label}
            </span>
          </button>
        ))}
      </div>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center bg-ivory">
        <div className="relative w-full min-h-screen overflow-hidden">
          {/* Background Video */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transform: 'translateZ(0)' }}
            onLoadedMetadata={(e) => {
              const video = e.currentTarget;
              video.currentTime = 20;
            }}
          >
            <source src="/Sunrise in the sea - whole video.mp4" type="video/mp4" />
          </video>
          
          {/* Subtle Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20 z-10"></div>

          {/* Hamburger Menu Button - Positioned on video */}
          <button
            onClick={toggleMenu}
            className="fixed top-6 right-6 z-50 w-12 h-12 flex flex-col justify-center items-center focus:outline-none group"
            aria-label="Menu"
          >
            <span
              className={`block w-8 md:w-10 h-0.5 bg-white shadow-lg transition-all duration-300 ease-in-out ${
                isMenuOpen ? 'rotate-90 translate-x-0 translate-y-0' : '-translate-x-2 -translate-y-2'
              }`}
            />
            <span
              className={`block w-8 md:w-10 h-0.5 bg-white shadow-lg transition-all duration-300 ease-in-out ${
                isMenuOpen ? 'rotate-0 translate-x-0 translate-y-0' : 'translate-x-2 translate-y-2'
              }`}
            />
          </button>

          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center px-4 py-4">
            <div className="flex flex-col items-center justify-center w-full max-w-5xl mx-auto">
            {/* Centered Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5 }}
              className="mb-4 md:mb-6 flex-shrink-0"
            >
              <img 
                src="/Holis Massage Logo transparent BG.png" 
                alt="Holis Massage" 
                className="w-40 sm:w-56 md:w-72 lg:w-80 h-auto opacity-90"
              />
            </motion.div>

            <div className="container-holis max-w-5xl flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.8, delay: 0.3 }}
            className="text-center space-y-3 md:space-y-4"
          >

            <p className="text-xs sm:text-sm md:text-base tracking-widest text-white/80 uppercase drop-shadow-md mb-2">
              {content?.hero?.subtitle || ''}
            </p>

            <h1 className="font-cormorant font-semibold text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-tight max-w-4xl mx-auto mb-3 px-4 text-white drop-shadow-lg">
              {(content?.hero?.title || '').split('\n').map((line: string, i: number) => (
                <React.Fragment key={i}>
                  {line}{i < (content?.hero?.title || '').split('\n').length - 1 && <br className="hidden sm:block" />}
                </React.Fragment>
              ))}
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-white/90 font-cormorant italic drop-shadow-md max-w-2xl mx-auto px-4">
              {(content?.hero?.tagline || '').split('\n').map((line: string, i: number) => (
                <React.Fragment key={i}>
                  {line}{i < (content?.hero?.tagline || '').split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-2 gap-4 items-start max-w-5xl mx-auto pt-2 px-4">
              {/* Left Column - Text */}
              <div className="text-center space-y-2">
                <p className="text-sm sm:text-base md:text-lg text-white font-cormorant drop-shadow-md">
                  {(content?.hero?.description || '').split('\n').map((line: string, i: number) => (
                    <React.Fragment key={i}>
                      {line}{i < (content?.hero?.description || '').split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              </div>

              {/* Right Column - Availability Boxes */}
              <div className="flex flex-col gap-2 justify-center md:justify-end">
                <div className="bg-white/90 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-md text-center shadow-lg max-w-xs mx-auto">
                  <p className="text-xs text-taupe mb-1">{content?.hero?.availability?.title || ''}</p>
                  <p className="text-sm font-medium">{content?.hero?.availability?.dates || ''}</p>
                  <p className="text-xs text-taupe">{content?.hero?.availability?.zurich || ''}</p>
                  {content?.hero?.availability?.stMoritz && (
                    <p className="text-xs text-taupe">{content.hero.availability.stMoritz}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center items-center pt-3 px-4">
              <a 
                href="https://wa.me/41796017337" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-charcoal text-ivory px-4 py-2 sm:px-8 sm:py-3 rounded-md hover:bg-bronze transition-colors w-full sm:w-auto text-center shadow-lg text-xs sm:text-base font-medium"
              >
                {content?.hero?.buttons?.primary || 'Request a Massage Experience'}
              </a>
              <a href="#complimentary" className="bg-white text-charcoal px-4 py-2 sm:px-6 sm:py-3 rounded-md hover:bg-sand transition-colors text-xs sm:text-sm w-full sm:w-auto text-center shadow-lg font-medium">
                {content?.hero?.buttons?.secondary || 'Complimentary 30-minute call'}
              </a>
            </div>
          </motion.div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maura Wasescha Testimonial - Below Hero */}
      <section className="py-16 sm:py-20 bg-ivory relative">
        <div className="container-holis">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4 }}
            className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12"
          >
            <div className="flex-shrink-0">
              <img 
                src="/testimonials.PNG" 
                alt="Maura Wasescha" 
                className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full object-cover object-top shadow-lg"
              />
            </div>
            <div className="text-center md:text-left">
              <span className="font-cormorant text-5xl text-bronze/30 leading-none">"</span>
              <p className="text-base sm:text-lg leading-relaxed italic text-taupe -mt-3">
                Don't expect a standard massage from David. You could rather call it a bodywork session. It is an experience that goes far beyond any sensory perception, a practice that touches all three levels: body, mind and soul. It is also a journey for those who wish to learn more about themselves and to heal on very deep levels, unlocking and freeing their own creativity and true potential. I have never experienced anything like it in my life.
              </p>
              <div className="mt-5">
                <div className="h-px w-12 bg-bronze/30 mx-auto md:mx-0 mb-3"></div>
                <p className="font-cormorant font-semibold text-lg text-charcoal">Maura Wasescha</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Recognition Section */}
      <section id="recognition" className="section-spacing bg-sand/30 relative overflow-hidden">
        {/* Decorative gradient blob */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-bronze/5 rounded-full blur-3xl"></div>
        <div className="container-holis relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4 }}
            className="max-w-2xl mx-auto space-y-6 text-center"
          >
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.3 }}
              className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl mb-8"
            >
              {content.recognition.title}
            </motion.h2>
            
            {content.recognition.paragraphs.map((para: string, i: number) => (
              <React.Fragment key={i}>
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, delay: 0.5 + i * 0.2 }}
                  className={`text-base sm:text-lg leading-relaxed ${i === 0 ? 'md:text-xl' : ''}`}
                >
                  {(para || '').split('\n').map((line: string, j: number) => (
                    <React.Fragment key={j}>
                      {line}{j < (para || '').split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </motion.p>
                {i < content.recognition.paragraphs.length - 1 && (
                  <div className="flex items-center justify-center my-8">
                    <div className="h-px w-16 bg-dark-green/30"></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="section-spacing relative overflow-hidden bg-bronze/10">
        {/* Decorative gradient blob */}
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-taupe/5 rounded-full blur-3xl"></div>
        <div className="container-holis relative z-10">
          <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl text-center mb-6">{content.experience.title}</h2>
          
          <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16 px-4 space-y-4">
            <p className="text-lg sm:text-xl leading-relaxed italic text-taupe">
              {content.experience.paragraphs[0]}
            </p>
          </div>

          {/* Process Carousel */}
          {(() => {
            const processBlocks = [
              ...(content.experience.paragraphs[1] || '').split('\n\n'),
              ...(content.experience.paragraphs[2] ? [content.experience.paragraphs[2]] : [])
            ];
            const totalSteps = processBlocks.length;
            return (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.0 }}
                className="max-w-3xl mx-auto mb-12 sm:mb-16 px-4"
              >
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                  {/* Header with title and step indicator */}
                  <div className="flex items-center justify-between px-6 sm:px-8 pt-6 sm:pt-8 pb-2">
                    <h3 className="font-cormorant text-xl sm:text-2xl font-medium text-charcoal">The Process</h3>
                    <span className="text-xs text-taupe tracking-wider uppercase">
                      {processStep + 1} / {totalSteps}
                    </span>
                  </div>

                  {/* Step dots */}
                  <div className="flex gap-1.5 px-6 sm:px-8 pb-4">
                    {processBlocks.map((_: string, i: number) => (
                      <button
                        key={i}
                        onClick={() => setProcessStep(i)}
                        className={`h-1 rounded-full transition-all duration-300 ${
                          i === processStep ? 'bg-bronze w-6' : 'bg-taupe/20 w-3'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Content area */}
                  <div className="px-6 sm:px-8 pb-4 min-h-[180px] flex items-start">
                    <motion.div
                      key={processStep}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-taupe leading-relaxed"
                    >
                      {(processBlocks[processStep] || '').split('\n').map((line: string, j: number) => (
                        <React.Fragment key={j}>
                          {line}{j < (processBlocks[processStep] || '').split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </motion.div>
                  </div>

                  {/* Navigation arrows */}
                  <div className="flex items-center justify-between px-6 sm:px-8 pb-6 sm:pb-8">
                    <button
                      onClick={() => setProcessStep(Math.max(0, processStep - 1))}
                      disabled={processStep === 0}
                      className={`flex items-center gap-1.5 text-sm transition-colors duration-200 ${
                        processStep === 0 ? 'text-taupe/30 cursor-default' : 'text-bronze hover:text-charcoal'
                      }`}
                    >
                      <ChevronLeft size={16} />
                      <span>Previous</span>
                    </button>
                    <button
                      onClick={() => setProcessStep(Math.min(totalSteps - 1, processStep + 1))}
                      disabled={processStep === totalSteps - 1}
                      className={`flex items-center gap-1.5 text-sm transition-colors duration-200 ${
                        processStep === totalSteps - 1 ? 'text-taupe/30 cursor-default' : 'text-bronze hover:text-charcoal'
                      }`}
                    >
                      <span>Next</span>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })()}

          {/* Benefits & Long-term - Compact Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto px-4">
            {/* What clients describe */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0 }}
              className="bg-white rounded-xl shadow-lg p-6 sm:p-8"
            >
              <h3 className="font-cormorant text-xl sm:text-2xl font-medium mb-5 text-charcoal">{content.experience.benefits.intro}</h3>
              <div className="space-y-3">
                {content.experience.benefits.items.map((item: string, i: number) => (
                  <div key={i} className="flex items-center gap-3 bg-sand/40 rounded-lg px-4 py-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-bronze flex-shrink-0"></span>
                    <p className="text-taupe text-sm sm:text-base">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* With Regular Work */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 sm:p-8 flex flex-col"
            >
              <h3 className="font-cormorant text-xl sm:text-2xl font-medium mb-5 text-charcoal">With Regular Work</h3>
              <div className="space-y-3 flex-1">
                {(content.experience.longTerm || '').split('\n').filter((line: string) => line.startsWith('–')).map((item: string, i: number) => (
                  <div key={i} className="flex items-center gap-3 bg-bronze/5 rounded-lg px-4 py-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-dark-green flex-shrink-0"></span>
                    <p className="text-taupe text-sm sm:text-base">{item.replace('– ', '')}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm sm:text-base leading-relaxed italic text-center text-taupe mt-6 pt-5 border-t border-taupe/10">
                {(content.experience.closing || '').split('\n').map((line: string, j: number) => (
                  <React.Fragment key={j}>
                    {line}{j < (content.experience.closing || '').split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section id="difference" className="section-spacing bg-sand/30 relative overflow-hidden">
        {/* Decorative gradient blob */}
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-bronze/5 rounded-full blur-3xl"></div>
        <div className="container-holis relative z-10">
          <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl text-center mb-8 sm:mb-12 px-4">Why This Feels Different</h2>
          
          <p className="text-base sm:text-lg leading-relaxed text-center max-w-2xl mx-auto mb-8 sm:mb-12 px-4">
            What makes this work rare is not technique.
          </p>

          {/* Cards Grid */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto px-4">
            {/* Card 1 - Conscious Touch */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.2 }}
              className="bg-sand p-6 sm:p-8 rounded-lg shadow-md"
            >
              <h3 className="font-cormorant text-lg sm:text-xl font-medium mb-3 sm:mb-4 text-charcoal">Conscious Touch</h3>
              <p className="text-taupe leading-relaxed">
                Respectful, present touch that honors your body and boundaries
              </p>
            </motion.div>

            {/* Card 2 - Emotional Attunement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.3 }}
              className="bg-bronze/20 p-6 sm:p-8 rounded-lg shadow-md"
            >
              <h3 className="font-cormorant text-lg sm:text-xl font-medium mb-3 sm:mb-4 text-charcoal">Deep Attunement</h3>
              <p className="text-taupe leading-relaxed">
                Emotional presence that meets you exactly where you are
              </p>
            </motion.div>

            {/* Card 3 - Gentle Guidance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.4 }}
              className="bg-dark-green/10 p-6 sm:p-8 rounded-lg shadow-md sm:col-span-2 md:col-span-1"
            >
              <h3 className="font-cormorant text-lg sm:text-xl font-medium mb-3 sm:mb-4 text-charcoal">Gentle Guidance</h3>
              <p className="text-taupe leading-relaxed">
                Support without authority, presence without demand
              </p>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.0, delay: 0.6 }}
            className="text-base sm:text-lg leading-relaxed text-center max-w-2xl mx-auto mt-8 sm:mt-12 italic text-taupe px-4"
          >
            You are not analysed. You are not pushed.<br />
            You are simply met — fully.
          </motion.p>
        </div>
      </section>

      {/* Beyond Technique Section */}
      <section id="beyond" className="section-spacing relative overflow-hidden bg-bronze/10">
        {/* Decorative gradient blob */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-taupe/5 rounded-full blur-3xl"></div>
        <div className="container-holis relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4 }}
            className="max-w-2xl mx-auto space-y-8 text-center"
          >
            <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl mb-8 sm:mb-12">Beyond Technique</h2>

            <p className="text-base sm:text-lg leading-relaxed">
              Some of my clients have explored the most renowned spas and practitioners worldwide.<br />
              They come expecting "another massage" — and leave surprised by something entirely different.
            </p>

            <p className="text-base sm:text-lg leading-relaxed">
              This work often reveals a kind of nourishment, presence, and inner peace<br />
              they did not know they were missing —<br />
              until their body finally felt it.
            </p>

            <p className="text-base sm:text-lg leading-relaxed italic text-taupe">
              If you are curious to explore what lies beyond technique and pressure,<br />
              you may discover an experience unlike anything you have known before.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section-spacing bg-sand/30 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-bronze/5 rounded-full blur-3xl"></div>
        <div className="container-holis relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl text-center mb-4">Testimonials</h2>
            <p className="text-sm text-taupe text-center mb-12 sm:mb-16">In their own words</p>

            {/* Video Testimonials - Side by Side */}
            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              {/* Sébastien Valla */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.0, delay: 0.3 }}
                className="bg-ivory p-5 sm:p-6 rounded-lg shadow-lg"
              >
                <div className="mb-4 rounded-lg overflow-hidden aspect-[9/16] max-h-[480px] mx-auto bg-charcoal/5">
                  <video
                    controls
                    preload="metadata"
                    className="w-full h-full object-contain rounded-lg"
                  >
                    <source src="/testimonial-sebastien-valla.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="text-center mb-3">
                  <p className="font-cormorant font-semibold text-lg">Sébastien Valla</p>
                  <p className="text-xs text-taupe mt-1">World-renowned teacher of Thai and Lomi Lomi massage</p>
                </div>
                <p className="text-sm leading-relaxed italic text-taupe text-center">
                  "Yesterday I received from David an amazing lomi lomi session. Really, my body, my mind, my thoughts travelled to Hawaii! The quality of touch is amazing! I highly recommend David's lomi lomi. You can go without thinking, and you're gonna have one of the best massages in the world!"
                </p>
              </motion.div>

              {/* Luisa Vargas */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.0, delay: 0.5 }}
                className="bg-ivory p-5 sm:p-6 rounded-lg shadow-lg"
              >
                <div className="mb-4 rounded-lg overflow-hidden aspect-[9/16] max-h-[480px] mx-auto bg-charcoal/5">
                  <video
                    controls
                    preload="metadata"
                    className="w-full h-full object-contain rounded-lg"
                  >
                    <source src="/testimonial-lu-vargas.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="text-center">
                  <p className="font-cormorant font-semibold text-lg">Luisa Vargas</p>
                  <p className="text-xs text-taupe mt-1">World Massage Champion 2023, IMA International Massage Association teacher and judge</p>
                </div>
              </motion.div>
            </div>

          </motion.div>
        </div>
      </section>

      {/* Amatrius Section */}
      <section id="amatrius" className="section-spacing relative overflow-hidden bg-bronze/10">
        {/* Decorative gradient blob */}
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-taupe/5 rounded-full blur-3xl"></div>
        <div className="container-holis relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4 }}
            className="max-w-2xl mx-auto space-y-6 text-center"
          >
            <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl mb-8 sm:mb-12">Sensory Support · Amatrius</h2>

            <p className="text-base sm:text-lg leading-relaxed">
              Depending on the session, I work with Amatrius aromatic compositions —<br />
              refined scents chosen intuitively to support relaxation, emotional opening,<br />
              and nervous-system regulation.
            </p>

            <p className="leading-relaxed">
              These scents are not decoration.<br />
              They activate the olfactory receptors directly connected to the nervous system,<br />
              supporting deeper presence and integration during the work.
            </p>

            <p className="text-lg leading-relaxed italic text-taupe">
              Used subtly, they become an added layer of support —<br />
              felt rather than explained.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Extended Work Section */}
      <section id="extended" className="section-spacing bg-sand/30 relative overflow-hidden">
        {/* Decorative gradient blob */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-bronze/5 rounded-full blur-3xl"></div>
        <div className="container-holis relative z-10">
          <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl text-center mb-12 sm:mb-16 px-4">Extended Work</h2>

          {/* Cards with Images Grid */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto mb-12 sm:mb-16 px-4">
            {/* Card 1 - Couples Work */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.2 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <img src="/couple.jpeg" alt="Couples Bodywork" className="h-40 sm:h-48 w-full object-cover" />
              <div className="p-6">
                <h3 className="font-cormorant text-lg sm:text-xl font-medium mb-3 text-charcoal">Couples Work</h3>
                <p className="text-taupe text-sm leading-relaxed">
                  Deepening relationships through conscious nurturing touch. Restoring safety, trust, and embodied presence.
                </p>
              </div>
            </motion.div>

            {/* Card 2 - Teaching */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.3 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <img src="/teaching.jpeg" alt="Teaching Session" className="h-40 sm:h-48 w-full object-cover" style={{ objectPosition: '50% 20%' }} />
              <div className="p-6">
                <h3 className="font-cormorant text-lg sm:text-xl font-medium mb-3 text-charcoal">Teaching & Transmission</h3>
                <p className="text-taupe text-sm leading-relaxed">
                  For practitioners and spa teams who wish to elevate their work from massaging the body to touching the whole human being.
                </p>
              </div>
            </motion.div>

            {/* Card 3 - Worldwide */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.4 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden sm:col-span-2 md:col-span-1"
            >
              <img src="/events.jpeg" alt="Private Session" className="h-40 sm:h-48 w-full object-cover" />
              <div className="p-6">
                <h3 className="font-cormorant text-lg sm:text-xl font-medium mb-3 text-charcoal">Worldwide Engagements</h3>
                <p className="text-taupe text-sm leading-relaxed">
                  Private events, luxury wellness days, retreats, and curated experiences for high-end hotels & spas worldwide.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Large Feature Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.0, delay: 0.6 }}
            className="bg-charcoal rounded-lg overflow-hidden shadow-2xl max-w-6xl mx-auto"
          >
            <div className="grid md:grid-cols-2">
              {/* Left - Text Content */}
              <div className="p-6 sm:p-10 md:p-12 flex flex-col justify-end">
                <h3 className="font-cormorant text-2xl sm:text-3xl font-semibold mb-4 sm:mb-6 text-ivory">The Art of Conscious Touch</h3>
                <p className="text-sm sm:text-base text-ivory/80 leading-relaxed mb-6">
                  Every session is a bridge to deeper presence, inner peace, and embodied aliveness. 
                  This work goes beyond technique — it's about meeting you fully, exactly as you are.
                </p>
                <a href="#contact" className="inline-block px-6 py-3 bg-transparent border border-ivory text-ivory rounded-md hover:bg-ivory/10 transition-colors w-fit">
                  Book Now
                </a>
              </div>
              {/* Right - Image */}
              <div className="h-64 sm:h-80 md:h-auto">
                <img src="/Monaco.jpg" alt="David Holis Session" className="w-full h-full object-cover" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-spacing bg-bronze/10">
        <div className="container-holis">
          <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl text-center mb-12 sm:mb-16 px-4">About David Holis</h2>

          <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 px-4">
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0 }}
              className="bg-white p-6 sm:p-8 rounded-lg shadow-md text-center"
            >
              <p className="text-base sm:text-lg leading-relaxed text-taupe">
                My work is shaped by <span className="font-medium text-charcoal">decades of dedicated practice and learning</span> with respected Hawaiian masters (kumu) and internationally recognised bodywork practitioners.
              </p>
            </motion.div>

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              {/* Left - Experience */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.0, delay: 0.2 }}
                className="bg-sand p-6 sm:p-8 rounded-lg shadow-md"
              >
                <h3 className="font-cormorant text-lg sm:text-xl font-medium mb-3 sm:mb-4 text-charcoal">Global Experience</h3>
                <p className="text-taupe leading-relaxed">
                  I have worked with and received valuable feedback from highly experienced therapists and teachers worldwide — some of whom later became clients themselves.
                </p>
              </motion.div>

              {/* Right - Award */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.0, delay: 0.3 }}
                className="bg-dark-green/10 p-6 sm:p-8 rounded-lg shadow-md border-l-4 border-bronze"
              >
                <h3 className="font-cormorant text-lg sm:text-xl font-medium mb-3 sm:mb-4 text-charcoal">Recognition</h3>
                <p className="text-taupe leading-relaxed">
                  In 2023, I was honoured to receive <span className="font-medium text-charcoal">1st Prize in the Wellness category</span> at the Swiss Massage Championship.
                </p>
              </motion.div>
            </div>

            {/* Approach */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.4 }}
              className="bg-white p-6 sm:p-10 rounded-lg shadow-md text-center"
            >
              <h3 className="font-cormorant text-xl sm:text-2xl font-medium mb-4 sm:mb-6 text-charcoal">My Approach Today</h3>
              <p className="text-base sm:text-lg leading-relaxed text-taupe mb-6">
                My work combines <span className="font-medium text-charcoal">conscious nurturing touch</span>, attentive dialogue, and refined sensory elements to support deep relaxation, inner peace, and embodied presence.
              </p>
              <div className="inline-block bg-bronze/10 px-6 sm:px-8 py-3 sm:py-4 rounded-md border border-bronze/30">
                <p className="text-base sm:text-lg font-medium text-charcoal">
                  I work privately and selectively with women who value discretion, depth, and authenticity.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-spacing bg-sand/30">
        <div className="container-holis">
          <div className="max-w-2xl mx-auto px-4">
            <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl text-center mb-12 sm:mb-16">Request a Private Experience</h2>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16"
              >
                <p className="text-2xl font-cormorant mb-4">Thank you</p>
                <p className="text-taupe">Your message has been received. I will respond personally within 48 hours.</p>
              </motion.div>
            ) : (
              <form onSubmit={handlePrivateSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm mb-2 text-taupe">Name</label>
                  <input
                    type="text"
                    required
                    value={privateForm.name}
                    onChange={(e) => setPrivateForm({ ...privateForm, name: e.target.value })}
                    className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-taupe">Email</label>
                  <input
                    type="email"
                    required
                    value={privateForm.email}
                    onChange={(e) => setPrivateForm({ ...privateForm, email: e.target.value })}
                    className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-taupe">Location</label>
                  <input
                    type="text"
                    required
                    value={privateForm.location}
                    onChange={(e) => setPrivateForm({ ...privateForm, location: e.target.value })}
                    className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-taupe">
                    If this bodywork experience could do "magic" in your life — private, professional, emotional, energetic, or mental — what would you hope it supports or changes?
                  </label>
                  <textarea
                    required
                    rows={6}
                    value={privateForm.message}
                    onChange={(e) => setPrivateForm({ ...privateForm, message: e.target.value })}
                    className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors resize-none"
                  />
                </div>

                <button type="submit" className="btn-primary block mx-auto">
                  Send Request
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Complimentary Session Section */}
      <section id="complimentary" className="section-spacing">
        <div className="container-holis">
          <div className="max-w-2xl mx-auto px-4">
            <h2 className="font-cormorant font-semibold text-3xl sm:text-4xl md:text-5xl text-center mb-4">Complimentary 30-Minute Call</h2>
            <p className="text-center text-taupe mb-8 text-lg leading-relaxed">
              A short phone or online conversation to explore what support would be most appropriate before deciding on any longer transformational work together.
            </p>
            <p className="text-center text-taupe/70 mb-12 sm:mb-16 text-sm">For ongoing Transformational Bodywork & Support</p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16"
              >
                <p className="text-2xl font-cormorant mb-4">Thank you</p>
                <p className="text-taupe">Your application has been received. I will respond personally within 48 hours.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleComplimentarySubmit} className="space-y-6">
                <div>
                  <label className="block text-sm mb-2 text-taupe">What brings you to apply at this point in your life?</label>
                  <textarea
                    required
                    rows={4}
                    value={complimentaryForm.question1}
                    onChange={(e) => setComplimentaryForm({ ...complimentaryForm, question1: e.target.value })}
                    className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-taupe">What feels most exhausting or under-nourished right now?</label>
                  <textarea
                    required
                    rows={4}
                    value={complimentaryForm.question2}
                    onChange={(e) => setComplimentaryForm({ ...complimentaryForm, question2: e.target.value })}
                    className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-taupe">Are you open to ongoing private bodywork and support if alignment is felt?</label>
                  <textarea
                    required
                    rows={3}
                    value={complimentaryForm.question3}
                    onChange={(e) => setComplimentaryForm({ ...complimentaryForm, question3: e.target.value })}
                    className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-2 text-taupe">Where are you currently based?</label>
                  <input
                    type="text"
                    required
                    value={complimentaryForm.question4}
                    onChange={(e) => setComplimentaryForm({ ...complimentaryForm, question4: e.target.value })}
                    className="w-full px-4 py-3 bg-ivory border border-taupe/20 rounded-md focus:outline-none focus:border-bronze transition-colors"
                  />
                </div>

                <button type="submit" className="btn-primary block mx-auto">
                  Submit Application
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-sand/30 border-t border-taupe/10">
        <div className="container-holis">
          <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-4">
            {/* Left Column - Brand */}
            <div>
              <p className="font-cormorant text-sm tracking-widest text-taupe mb-1">
                HOLIS MASSAGE — Conscious Nurturing Touch
              </p>
              <p className="text-xs text-taupe/70">Zurich · St. Moritz · Worldwide</p>
              <div className="flex items-center gap-3 mt-2">
                <a href="https://wa.me/41796017337" target="_blank" rel="noopener noreferrer" className="text-taupe/60 hover:text-bronze transition-colors text-xs">
                  WhatsApp
                </a>
                <span className="text-taupe/30">·</span>
                <a href="mailto:info@holismassage.com" className="text-taupe/60 hover:text-bronze transition-colors text-xs">
                  Email
                </a>
              </div>
            </div>

            {/* Right Column - Legal */}
            <div className="md:text-right">
              <div className="flex flex-wrap gap-3 md:justify-end text-xs text-taupe/60">
                <a href="/privacy-policy" className="hover:text-bronze transition-colors">Privacy Policy</a>
                <span className="text-taupe/30">·</span>
                <a href="/privacy-policy#cookies" className="hover:text-bronze transition-colors">Cookie Policy</a>
                <span className="text-taupe/30">·</span>
                <a href="/privacy-policy#gdpr" className="hover:text-bronze transition-colors">GDPR</a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-taupe/10 pt-3 flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-xs text-taupe/50">
              © {new Date().getFullYear()} Holis Massage. All rights reserved.
            </p>
            <p className="text-xs text-taupe/50">
              Created by <a href="https://wavelynecode.com" target="_blank" rel="noopener noreferrer" className="hover:text-bronze transition-colors">Wavelyne</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
